import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
} from '@cinch-labs/design-system'
import type { VehicleListing } from '@cinch-nx/data-search'
import type { DirectVehicleAdvertSummary } from '@cinch-nx/data-vehicle'
import React, { useMemo } from 'react'
import { BrowseAllCarsCard } from '../../custom-cards/browse-all-cars-card'
import VehicleCard from '../../vehicle-card/vehicle-card'
import { useCarsCarouselTracking } from './carousel-with-controls'
import styles from './carousel-with-controls.module.css'
import { BrandNewVehicleCard } from '../../brand-new-vehicle-card/brand-new-vehicle-card'
interface CarouselWithControlsProps {
  vehicles?: VehicleListing[]
  onSaveButtonClick: (vehicleId: string) => void
  favourites: DirectVehicleAdvertSummary[]
  showBrowseAllCarsCard?: boolean
  showMonthlyPrices: boolean
}

interface CardProps {
  vehicle: VehicleListing
  onSaveButtonClick: (vehicleId: string) => void
  favourites: DirectVehicleAdvertSummary[]
}

const Card: React.FC<CardProps> = ({
  vehicle,
  onSaveButtonClick,
  favourites,
}) => {
  const trackingEvents = useCarsCarouselTracking()

  const vehicleCardId = vehicle.vehicleId
  const isSaved = favourites
    ? favourites?.some((vehicle) => vehicle.vehicleId === vehicleCardId)
    : false

  const handleOnSave = () => {
    onSaveButtonClick(vehicle.vehicleId)

    if (!isSaved) {
      trackingEvents.onAddSaveVehicleClick()
    } else {
      trackingEvents.onRemoveSaveVehicleClick()
    }
  }

  return (
    <VehicleCard
      onSave={handleOnSave}
      key={vehicle.vehicleId}
      isSaved={isSaved}
      isPending={false}
      isAvailable={true}
      vehicle={vehicle as VehicleListing & DirectVehicleAdvertSummary}
      id={vehicle.vehicleId}
    />
  )
}

interface Slide {
  key: string
  card?: JSX.Element
}

export const CarouselWithControls: React.FC<CarouselWithControlsProps> = ({
  vehicles,
  onSaveButtonClick,
  favourites,
  showBrowseAllCarsCard,
  showMonthlyPrices,
}) => {
  const carouselId = useMemo(
    () => 'carousel-' + Math.random().toString(36).slice(2, 15),
    [],
  )

  const vehicleSlides: Slide[] = vehicles
    ? vehicles.map((vehicle) => ({
        key: vehicle.vehicleId,
        card:
          vehicle.condition === 'used' ? (
            <Card
              vehicle={vehicle}
              onSaveButtonClick={onSaveButtonClick}
              favourites={favourites}
            />
          ) : (
            <BrandNewVehicleCard
              isPending={false}
              key={vehicle.vehicleId}
              id={vehicle.vehicleId}
              showMonthlyPrice={showMonthlyPrices}
              {...vehicle}
            />
          ),
      }))
    : []
  const customSlides: Slide[] = showBrowseAllCarsCard
    ? [{ key: 'browse-all', card: <BrowseAllCarsCard /> }]
    : []
  const slides = [...vehicleSlides, ...customSlides]

  return (
    <>
      <Carousel
        name={carouselId}
        customClassNames={{ carousel: styles.carousel }}
        swiperSettings={{
          slidesPerView: 'auto',
          spaceBetween: 24,
        }}
      >
        {slides?.map((slide, index) => (
          <CarouselSlide key={index} className={styles.carouselSlide}>
            <div key={slide.key} className={styles.cardWrapper}>
              {slide.card}
            </div>
          </CarouselSlide>
        ))}
      </Carousel>
      <CarouselControls customClassNames={styles.controlsWrapper}>
        <CarouselPagination
          name={carouselId}
          customClassNames={styles.customPagination}
        />
        <CarouselNavigation
          name={carouselId}
          ariaLabels={{
            previous: 'Go to previous slide',
            next: 'Go to next slide',
          }}
        />
      </CarouselControls>
    </>
  )
}
